// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { EvolveApiRoute, EvolveApiResponse } from '@evolvemep/api-ts';
import { useUncontrolled } from '@mantine/hooks';

import type { SearchableMultiSelectProps } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableMultiSelect';
import type { SearchableSelectProps } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/SearchableSelect';
import { useSearchableData } from 'components/Mantine/TypeSafeSelect/PaginatedSelectors/useSearchableData';
import type { TypeSafeGroupComboboxData } from 'components/Mantine/TypeSafeSelect/types';
import type { TypeSafeInternalValue } from 'components/Mantine/TypeSafeSelect/useWrappedSelect';
import { hasLengthExactly, type StrongOmit, type KeysOfType } from 'helpers/helper-types';
import { isNil, isNotNil } from 'helpers/isNotNil';

type Props<
  Route extends EvolveApiRoute<'GET_PAGINATED'>,
  T extends Partial<EvolveApiResponse<'GET_PAGINATED', Route>['data'][number]>,
  TKey extends KeysOfType<T, string>,
> = StrongOmit<
  SearchableSelectProps<Route, T, TKey> & SearchableMultiSelectProps<Route, T, TKey>,
  'value' | 'defaultValue' | 'onChange' | 'vars' | 'hidePickedOptions'
>;

export const useSearchableSelect = <
  Route extends EvolveApiRoute<'GET_PAGINATED'>,
  T extends Partial<EvolveApiResponse<'GET_PAGINATED', Route>['data'][number]>,
  TKey extends KeysOfType<T, string>,
>(
  props: TypeSafeInternalValue<T>,
  selectProps: Props<Route, T, TKey>,
) => {
  const hasValue = props.type === 'multi' ? props.__value.length > 0 : isNotNil(props.__value);
  const {
    dropdownOpened: parentDropdownOpened,
    onDropdownOpen: parentOnDropdownOpen,
    onDropdownClose: parentOnDropdownClose,
    paginatedGet,
    searchKey,

    useLike,
    disableSearch = false,
    searchValue,
    onSearchChange,
    autoSelectWhenOnlyOneOption,
    preloadData = (autoSelectWhenOnlyOneOption && !hasValue) ?? false,
    getItemLabel,
    idKey,
    getGroup,
    disableOption,
    excludeOption,
    loading: parentLoading,
    ...rest
  } = selectProps;
  const isFirstPull = useRef(true);

  const [__dropdownOpened, __setDropdownOpened] = useUncontrolled({
    value: parentDropdownOpened,
    onChange: (v) => {
      if (v) parentOnDropdownOpen?.();
      else parentOnDropdownClose?.();
    },
  });

  const onDropdownOpen = useCallback(() => __setDropdownOpened(true), [__setDropdownOpened]);
  const onDropdownClose = useCallback(() => __setDropdownOpened(false), [__setDropdownOpened]);

  const { options, setIsLazy, loading, setSearchPhrase, fetchNextPage } = useSearchableData<Route, T, TKey, string>({
    paginatedGet,
    searchKey,
    useLike,
    lazy: true,
    excludeOption,
  });

  // If false, then the user is currently typing, and we haven't done another search yet
  const [latestDataLoaded, setLatestDataLoaded] = useState(!disableSearch);
  useEffect(() => setLatestDataLoaded(!loading), [loading]);

  const [__searchValue, __onSearchChange] = useUncontrolled({
    value: searchValue,
    defaultValue: '',
    onChange: (v) => {
      onSearchChange?.(v);
      setLatestDataLoaded(disableSearch || __searchValue === v);
      setSearchPhrase(v);
    },
  });

  useEffect(() => {
    if (__dropdownOpened) {
      setIsLazy(disableSearch);
    } else {
      setIsLazy(!preloadData);
    }
  }, [disableSearch, __dropdownOpened, preloadData, setIsLazy]);

  useEffect(() => {
    if (isFirstPull.current && options.length > 0) {
      isFirstPull.current = false;
      if (autoSelectWhenOnlyOneOption && hasLengthExactly(options, 1)) {
        if (props.type === 'multi') {
          props.__onChange(options);
        } else {
          props.__onChange(options[0]);
        }
        onDropdownClose();
      }
    }
  }, [autoSelectWhenOnlyOneOption, props, options, onDropdownClose]);

  const data = useMemo(() => {
    const existingValues = Array.isArray(props.__value)
      ? props.__value
      : isNotNil(props.__value)
        ? [props.__value]
        : [];
    const opts = [
      ...existingValues.map((t) => ({
        original: t,
        label: getItemLabel(t),
        value: t[idKey] as string,
      })),
      ...options
        .filter((t) => !existingValues.some((st) => st[idKey] === t[idKey]))
        .map((t) => ({
          original: t,
          label: getItemLabel(t),
          value: t[idKey] as string,
          disabled: disableOption?.(t),
        })),
    ];
    if (isNil(getGroup)) return opts;

    const groupedData: TypeSafeGroupComboboxData<string, { original: T }>[] = [];
    opts.forEach((opt) => {
      const group = getGroup(opt.original) ?? '';
      const existing = groupedData.find((d) => d.group === group);
      if (!existing) {
        groupedData.push({
          group,
          items: [opt],
        });
      } else {
        existing.items.push(opt);
      }
    });
    return groupedData;
  }, [props.__value, getGroup, options, getItemLabel, idKey, disableOption]);

  const overrideKeys = useMemo(() => {
    const restKeys = Object.keys(rest);
    return Object.keys(selectProps).filter((k) => !restKeys.includes(k));
  }, [rest, selectProps]);

  return {
    options,
    latestDataLoaded,
    isFirstPull,
    data,
    fetchNextPage,
    /**
     * This is the list of keys used by the `useSearchableSelect` hook,
     * and therefore should not be spread onto the SearchableSelect/SearchableMultiSelect
     */
    overrideKeys,
    overrideProps: {
      loading: parentLoading || (loading && (__dropdownOpened || (autoSelectWhenOnlyOneOption && isFirstPull.current))),
      dropdownOpened: __dropdownOpened,
      onDropdownOpen,
      onDropdownClose,
      searchValue: __searchValue,
      onSearchChange: __onSearchChange,
    },
  };
};
